import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Align, Appearance, FontWeight } from 'design/types';

interface IMessageProps {
  appearance?: Appearance.WARNING | Appearance.DANGER | Appearance.SUCCESS | Appearance.NEUTRAL;
  className?: string;
  dataCy?: string;
  fitContent?: boolean;
  fontWeight?: FontWeight;
  title?: string;
  message: string | ReactNode;
}

const Layout = styled.div<Partial<IMessageProps>>`
  display: flex;
  flex-flow: column nowrap;
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(14)};
  background-color: ${theme.color.neutral[100]};
  border-radius: ${toRem(8)};
  color: ${theme.color.neutral[900]};
  line-height: ${toRem(21)};
  width: 100%;
  padding: ${toRem(22)};
  word-break: break-word;

  ${({ appearance }) =>
    (appearance === Appearance.SUCCESS || appearance === Appearance.WARNING || appearance === Appearance.DANGER) &&
    css`
      background-color: ${theme.color[appearance].light};
    `};

  ${({ fitContent }) =>
    fitContent &&
    css`
      width: fit-content;
      height: fit-content;
    `};
`;

const Title = styled.h4<{ appearance?: Appearance }>`
  color: ${({ appearance }) => (appearance === Appearance.DANGER ? theme.color.danger.medium : 'inherit')};
  font-weight: ${theme.font.weight.semiBold};
`;

const Content = styled.p<{ fontWeight?: FontWeight; hasTitle?: boolean }>`
  ${({ hasTitle }) =>
    !hasTitle &&
    css`
      justify-content: ${Align.CENTER};
      align-items: ${Align.CENTER};
      text-align: ${Align.CENTER};
    `};

  ${({ fontWeight }) =>
    fontWeight &&
    css`
      font-weight: ${fontWeight};
    `};
`;

export const Message = ({ appearance, className = '', dataCy = 'message', fontWeight, fitContent = false, title, message: content }: IMessageProps) => {
  const isStringContent = typeof content === 'string';
  const hasTitle = !!title;
  return (
    <Layout data-cy={dataCy} className={className} appearance={appearance} fitContent={fitContent}>
      {title && (
        <Title data-cy={`${dataCy}-title`} appearance={appearance}>
          {title}
        </Title>
      )}
      <Content data-cy={`${dataCy}-content`} fontWeight={fontWeight} as={isStringContent ? 'p' : 'div'} hasTitle={hasTitle}>
        {content}
      </Content>
    </Layout>
  );
};
